import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, Layers, Calendar } from "lucide-react";
import { useProductionPeople } from "../hooks/useProductionPeople";
import { t } from "@/shared/lib/th-translations";
import {
  computeZScore,
  zScoreColor,
  hasEnoughBaseline,
  formatMetricDisplay,
} from "@/frontend/lib/production-stats";
import type { PeopleActivitySummary, PeopleContribution } from "@/shared/types/production";
import { categorizeMachine } from "@/frontend/lib/machine-categories";
import { getProcessOrderIndex } from "@/shared/constants";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import ZScoreLegend from "../components/production/ZScoreLegend";
import PeopleContributionsModal from "../components/production/PeopleContributionsModal";

type WindowDays = 1 | 7 | 14 | 30;

const windowOptions: { label: string; value: WindowDays }[] = [
  { label: "production.people.window.today", value: 1 },
  { label: "production.people.window.last7Days", value: 7 },
  { label: "production.people.window.last14Days", value: 14 },
  { label: "production.people.window.last30Days", value: 30 },
];

type PeopleMetric = "good_units" | "shots" | "value" | "paper_length" | "paper_area";

const peopleMetricOptions: { label: string; value: PeopleMetric }[] = [
  { label: "production.people.metric.goodUnits", value: "good_units" },
  { label: "production.people.metric.shots", value: "shots" },
  { label: "production.people.metric.value", value: "value" },
  { label: "production.people.metric.paperLength", value: "paper_length" },
  { label: "production.people.metric.paperArea", value: "paper_area" },
];

const THB_FORMATTER = new Intl.NumberFormat("th-TH", {
  style: "currency",
  currency: "THB",
  maximumFractionDigits: 0,
});

function formatTHB(n: number): string {
  return THB_FORMATTER.format(n);
}

interface BaselineStatsLite {
  mean: number;
  stddev: number;
  count: number;
}

interface HeatmapRow {
  key: string;
  employeeId: string;
  employeeName: string;
  machine: string | null; // null when not split
  primaryProcess: string;
  dailyGoodUnits: Record<string, number>;
  dailyShots: Record<string, number>;
  dailyValue: Record<string, number>;
  dailyPaperLength: Record<string, number>;
  dailyPaperArea: Record<string, number>;
  totalGoodUnits: number;
  totalShots: number;
  totalValue: number;
  totalPaperLength: number;
  totalPaperArea: number;
  activeDays: number;
  uniqueMachines: number;
  primaryProcessShare: number;
  baseline: BaselineStatsLite;
  baselineShots: BaselineStatsLite;
  baselinePaperLength: BaselineStatsLite;
  baselinePaperArea: BaselineStatsLite;
  // All in-window log events that fed this row's numbers. Filtered to the row's
  // machine when machineSplit is on. Used by the click-drill modal.
  contributions: PeopleContribution[];
}

function statsFromDailyValues(values: number[]): BaselineStatsLite {
  const n = values.length;
  if (n === 0) return { mean: 0, stddev: 0, count: 0 };
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  return { mean, stddev: Math.sqrt(variance), count: n };
}

function buildRows(
  people: PeopleActivitySummary[],
  machineSplit: boolean,
  days: string[],
): HeatmapRow[] {
  if (!machineSplit) {
    return people.map((p) => ({
      key: p.employeeId,
      employeeId: p.employeeId,
      employeeName: p.employeeName,
      machine: null,
      primaryProcess: p.primaryProcess,
      dailyGoodUnits: p.dailyGoodUnits,
      dailyShots: p.dailyShots,
      dailyValue: p.dailyValue,
      dailyPaperLength: p.dailyPaperLength,
      dailyPaperArea: p.dailyPaperArea,
      totalGoodUnits: p.totalGoodUnits,
      totalShots: p.totalShots,
      totalValue: p.totalValue,
      totalPaperLength: p.totalPaperLength,
      totalPaperArea: p.totalPaperArea,
      activeDays: p.activeDays,
      uniqueMachines: p.uniqueMachines,
      primaryProcessShare: p.primaryProcessShare,
      baseline: p.baseline,
      baselineShots: p.baselineShots,
      baselinePaperLength: p.baselinePaperLength,
      baselinePaperArea: p.baselinePaperArea,
      contributions: p.contributions ?? [],
    }));
  }
  const rows: HeatmapRow[] = [];
  for (const p of people) {
    for (const mb of p.byMachine) {
      const guValues = days.map((d) => mb.dailyGoodUnits[d] ?? 0);
      const shotsValues = days.map((d) => mb.dailyShots[d] ?? 0);
      const plValues = days.map((d) => mb.dailyPaperLength[d] ?? 0);
      const paValues = days.map((d) => mb.dailyPaperArea[d] ?? 0);
      const machineContributions = (p.contributions ?? []).filter(
        (c) => c.machine === mb.machine,
      );
      rows.push({
        key: `${p.employeeId}||${mb.machine}`,
        employeeId: p.employeeId,
        employeeName: p.employeeName,
        machine: mb.machine,
        primaryProcess: mb.primaryProcess,
        dailyGoodUnits: mb.dailyGoodUnits,
        dailyShots: mb.dailyShots,
        dailyValue: mb.dailyValue,
        dailyPaperLength: mb.dailyPaperLength,
        dailyPaperArea: mb.dailyPaperArea,
        totalGoodUnits: mb.totalGoodUnits,
        totalShots: mb.totalShots,
        totalValue: mb.totalValue,
        totalPaperLength: mb.totalPaperLength,
        totalPaperArea: mb.totalPaperArea,
        activeDays: mb.activeDays,
        uniqueMachines: 1,
        primaryProcessShare: 1,
        baseline: statsFromDailyValues(guValues),
        baselineShots: statsFromDailyValues(shotsValues),
        baselinePaperLength: statsFromDailyValues(plValues),
        baselinePaperArea: statsFromDailyValues(paValues),
        contributions: machineContributions,
      });
    }
  }
  return rows;
}

interface Group {
  key: string;
  title: string;
  icon?: string;
  items: HeatmapRow[];
}

function groupRowsByProcess(rows: HeatmapRow[]): Group[] {
  const buckets = new Map<string, HeatmapRow[]>();
  for (const r of rows) {
    const key = r.primaryProcess || t("production.people.unknownProcess");
    const arr = buckets.get(key) ?? [];
    arr.push(r);
    buckets.set(key, arr);
  }
  return Array.from(buckets.entries())
    .map(([process, items]) => ({ key: process, title: process, items }))
    .sort((a, b) => {
      const ai = getProcessOrderIndex(a.title);
      const bi = getProcessOrderIndex(b.title);
      if (ai !== bi) return ai - bi;
      return a.title.localeCompare(b.title, "th");
    });
}

function groupRowsByMachine(rows: HeatmapRow[]): Group[] {
  const buckets = new Map<string, HeatmapRow[]>();
  for (const r of rows) {
    const key = r.machine || "—";
    const arr = buckets.get(key) ?? [];
    arr.push(r);
    buckets.set(key, arr);
  }
  return Array.from(buckets.entries())
    .map(([machine, items]) => {
      const cat = machine && machine !== "—" ? categorizeMachine(machine) : null;
      const sorted = [...items].sort(
        (a, b) => b.totalGoodUnits - a.totalGoodUnits,
      );
      return {
        key: machine,
        title: machine,
        icon: cat?.icon,
        items: sorted,
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title, "th", { numeric: true }));
}

type GroupBy = "process" | "machine";

const groupByOptions: { label: string; value: GroupBy }[] = [
  { label: "production.people.group.process", value: "process" },
  { label: "production.people.group.machine", value: "machine" },
];

export default function ProductionPeople() {
  const [windowDays, setWindowDays] = useState<WindowDays>(14);
  const [metric, setMetric] = useState<PeopleMetric>("shots");
  const [machineSplit, setMachineSplit] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>("process");
  const [drill, setDrill] = useState<
    { row: HeatmapRow; day: string | null } | null
  >(null);
  const { data, isLoading, error } = useProductionPeople(windowDays);

  // machine grouping requires per-(person, machine) rows
  const effectiveSplit = machineSplit || groupBy === "machine";

  const responseData = data?.data;
  const days = responseData?.days ?? [];
  const people = useMemo(
    () =>
      [...(responseData?.people ?? [])].sort((a, b) =>
        a.employeeName.localeCompare(b.employeeName, "th"),
      ),
    [responseData],
  );

  const totalPeople = responseData?.totalPeople ?? 0;
  const totalEvents = responseData?.totalEvents ?? 0;
  const avgActiveDays = useMemo(() => {
    if (people.length === 0) return 0;
    return people.reduce((sum, p) => sum + p.activeDays, 0) / people.length;
  }, [people]);

  const rows = useMemo(
    () => buildRows(people, effectiveSplit, days),
    [people, effectiveSplit, days],
  );
  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const byName = a.employeeName.localeCompare(b.employeeName, "th");
        if (byName !== 0) return byName;
        return (a.machine ?? "").localeCompare(b.machine ?? "", "th", {
          numeric: true,
        });
      }),
    [rows],
  );
  const groups = useMemo(
    () =>
      groupBy === "machine"
        ? groupRowsByMachine(sortedRows)
        : groupRowsByProcess(sortedRows),
    [sortedRows, groupBy],
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">{t("navigation.productionPeople")}</h1>
        <p className="text-muted-foreground">{t("production.people.subtitle")}</p>
      </div>

      {/* Caveat — own-baseline comparison, not peer */}
      <Alert>
        <AlertDescription className="text-sm text-muted-foreground">
          {t("production.people.caveat")}
        </AlertDescription>
      </Alert>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.people.window.label")}:
          </span>
          <div className="flex gap-1">
            {windowOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={windowDays === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setWindowDays(opt.value)}
              >
                {t(opt.label)}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.people.metric.label")}:
          </span>
          <div className="flex gap-1">
            {peopleMetricOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={metric === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setMetric(opt.value)}
              >
                {t(opt.label)}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {t("production.people.group.label")}:
          </span>
          <div className="flex gap-1">
            {groupByOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={groupBy === opt.value ? "default" : "outline"}
                size="sm"
                onClick={() => setGroupBy(opt.value)}
              >
                {t(opt.label)}
              </Button>
            ))}
          </div>
        </div>
        {groupBy === "process" && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {t("production.people.split.label")}:
            </span>
            <Button
              variant={machineSplit ? "default" : "outline"}
              size="sm"
              onClick={() => setMachineSplit((v) => !v)}
            >
              {machineSplit
                ? t("production.people.split.on")
                : t("production.people.split.off")}
            </Button>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.stats.totalPeople")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                totalPeople.toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.stats.totalEvents")}
            </CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                totalEvents.toLocaleString()
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("production.people.stats.avgActiveDays")}
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                avgActiveDays.toFixed(1)
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{t("production.people.error")}</AlertDescription>
        </Alert>
      )}

      {/* Main heatmap grouped by primary process */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          {t("production.people.empty")}
        </div>
      ) : (
        <div className="space-y-4">
          <ZScoreLegend namespace="production.people.legend" showNoData />
          {groups.map(({ key, title, icon, items }) => (
            <Card key={key}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  {icon && (
                    <span className="text-xl" aria-hidden>
                      {icon}
                    </span>
                  )}
                  {groupBy === "machine" ? (
                    <Link
                      to={`/production/machines/${encodeURIComponent(title)}`}
                      className="text-blue-600 hover:underline"
                    >
                      {title}
                    </Link>
                  ) : (
                    <span>{title}</span>
                  )}
                  <span className="text-xs font-normal text-muted-foreground">
                    {groupBy === "machine"
                      ? `(${t("production.people.table.operators")}: ${items.length})`
                      : `(${items.length})`}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <PeopleHeatmapTable
                  rows={items}
                  days={days}
                  metric={metric}
                  machineSplit={effectiveSplit}
                  hideMachineTag={groupBy === "machine"}
                  onOpenDrill={(row, day) => setDrill({ row, day })}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {drill && (
        <PeopleContributionsModal
          open
          onClose={() => setDrill(null)}
          employeeName={drill.row.employeeName}
          day={drill.day}
          rows={drill.row.contributions}
          machine={drill.row.machine}
        />
      )}
    </div>
  );
}

interface HeatmapTableProps {
  rows: HeatmapRow[];
  days: string[];
  metric: PeopleMetric;
  machineSplit: boolean;
  hideMachineTag?: boolean;
  onOpenDrill?: (row: HeatmapRow, day: string | null) => void;
}

function peopleDailyVal(row: HeatmapRow, day: string, metric: PeopleMetric): number {
  if (metric === "shots") return row.dailyShots?.[day] ?? 0;
  if (metric === "value") return row.dailyValue?.[day] ?? 0;
  if (metric === "paper_length") return row.dailyPaperLength?.[day] ?? 0;
  if (metric === "paper_area") return row.dailyPaperArea?.[day] ?? 0;
  return row.dailyGoodUnits[day] ?? 0;
}

function peopleTotalVal(row: HeatmapRow, metric: PeopleMetric): number {
  if (metric === "shots") return row.totalShots;
  if (metric === "value") return row.totalValue;
  if (metric === "paper_length") return row.totalPaperLength;
  if (metric === "paper_area") return row.totalPaperArea;
  return row.totalGoodUnits;
}

function peopleBaselineRef(row: HeatmapRow, metric: PeopleMetric): BaselineStatsLite {
  if (metric === "shots") return row.baselineShots;
  if (metric === "paper_length") return row.baselinePaperLength;
  if (metric === "paper_area") return row.baselinePaperArea;
  return row.baseline;
}

const PEOPLE_NO_ZSCORE = new Set<PeopleMetric>(["value"]);

function PeopleHeatmapTable({
  rows,
  days,
  metric,
  machineSplit,
  hideMachineTag,
  onOpenDrill,
}: HeatmapTableProps) {
  const dailySums: Record<string, number> = {};
  let grandTotal = 0;
  for (const day of days) dailySums[day] = 0;
  for (const row of rows) {
    for (const day of days) {
      dailySums[day] += peopleDailyVal(row, day, metric);
    }
    grandTotal += peopleTotalVal(row, metric);
  }
  const fmtCell = (v: number) =>
    metric === "value" ? formatTHB(v) : formatMetricDisplay(v, metric);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="sticky left-0 bg-gray-50 px-4 py-2 text-left font-medium whitespace-nowrap z-10 border-r">
              {machineSplit && !hideMachineTag
                ? t("production.people.table.personMachineCol")
                : t("production.people.table.personCol")}
            </th>
            {days.map((day) => (
              <th
                key={day}
                className="px-2 py-2 text-center font-medium whitespace-nowrap min-w-[56px]"
              >
                {day.slice(5)}
              </th>
            ))}
            <th className="px-4 py-2 text-right font-medium whitespace-nowrap border-l">
              {t("production.people.table.totalCol")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b hover:bg-gray-50/50">
              <td className="sticky left-0 bg-white px-4 py-2 whitespace-nowrap z-10 border-r">
                <div className="font-medium flex items-center gap-2">
                  <Link
                    to={`/production/people/${encodeURIComponent(row.employeeId)}`}
                    className="hover:underline text-blue-600"
                  >
                    {row.employeeName}
                  </Link>
                  {row.machine && !hideMachineTag && (
                    <Link
                      to={`/production/machines/${encodeURIComponent(row.machine)}`}
                      className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      {row.machine}
                    </Link>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {row.machine ? (
                    <>
                      {t("production.people.table.activeDaysCol")}: {row.activeDays}
                    </>
                  ) : (
                    <>
                      {t("production.people.table.machinesCol")}: {row.uniqueMachines}
                      {" · "}
                      {t("production.people.table.activeDaysCol")}: {row.activeDays}
                      {" · "}
                      {t("production.people.table.primaryShare")}:{" "}
                      {Math.round(row.primaryProcessShare * 100)}%
                    </>
                  )}
                </div>
              </td>

              {days.map((day) => {
                const cellValue = peopleDailyVal(row, day, metric);
                const hasData = cellValue > 0;

                if (!hasData) {
                  return (
                    <td
                      key={day}
                      className="px-2 py-2 text-center bg-gray-100 text-gray-400 text-xs"
                      title={t("production.people.table.emptyDay")}
                    >
                      —
                    </td>
                  );
                }

                const baselineRef = peopleBaselineRef(row, metric);
                const enough = hasEnoughBaseline(baselineRef.count);
                const skipZScore = PEOPLE_NO_ZSCORE.has(metric);
                const z = enough && !skipZScore
                  ? computeZScore(cellValue, baselineRef.mean, baselineRef.stddev)
                  : 0;
                const colorClass = skipZScore
                  ? "bg-emerald-50 text-emerald-900"
                  : enough
                    ? zScoreColor(z)
                    : "bg-gray-200 text-gray-700";
                const valueLabel = t(`production.people.metric.${
                  metric === "good_units" ? "goodUnits" : metric === "paper_length" ? "paperLength" : metric === "paper_area" ? "paperArea" : metric
                }`);
                const cellDisplay =
                  metric === "value"
                    ? formatTHB(cellValue)
                    : formatMetricDisplay(cellValue, metric);

                const tooltipLines = [
                  `${t("production.people.tooltip.day")}: ${day}`,
                  `${t("production.people.tooltip.person")}: ${row.employeeName}`,
                  row.machine
                    ? `${t("production.people.tooltip.machine")}: ${row.machine}`
                    : null,
                  `${valueLabel}: ${cellDisplay}`,
                  enough && !skipZScore
                    ? `${t("production.people.tooltip.ownMean")}: ${formatMetricDisplay(baselineRef.mean, metric)}`
                    : null,
                  enough && !skipZScore
                    ? `${t("production.people.tooltip.zScore")}: ${z.toFixed(2)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join("\n");

                return (
                  <td
                    key={day}
                    className={`px-2 py-2 text-center text-xs font-medium ${colorClass} ${onOpenDrill ? "cursor-pointer hover:ring-2 hover:ring-blue-400" : ""}`}
                    title={tooltipLines}
                    onClick={onOpenDrill ? () => onOpenDrill(row, day) : undefined}
                  >
                    {cellDisplay}
                  </td>
                );
              })}

              <td
                className={`px-4 py-2 text-right font-semibold whitespace-nowrap border-l ${onOpenDrill ? "cursor-pointer hover:bg-blue-50" : ""}`}
                onClick={onOpenDrill ? () => onOpenDrill(row, null) : undefined}
              >
                {metric === "value"
                  ? formatTHB(peopleTotalVal(row, metric))
                  : formatMetricDisplay(peopleTotalVal(row, metric), metric)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 bg-gray-100 font-semibold">
            <td className="sticky left-0 bg-gray-100 px-4 py-2 whitespace-nowrap z-10 border-r">
              {t("production.people.table.sumRow")}
            </td>
            {days.map((day) => (
              <td
                key={day}
                className="px-2 py-2 text-center text-xs whitespace-nowrap tabular-nums"
              >
                {dailySums[day] > 0 ? fmtCell(dailySums[day]) : "—"}
              </td>
            ))}
            <td className="px-4 py-2 text-right whitespace-nowrap border-l tabular-nums">
              {fmtCell(grandTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
